import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  rememberToastFired, wasToastRecentlyFired,
  rememberToastDismissed, wasToastDismissed,
} from '../lib/dismiss-registry.js';

// ============================================================
// ToastCenter — app-wide live notification stack
// ============================================================
// Renders a top-right column of slide-in toast cards. Any component
// can fire a toast via `useToast()` — typical use is the synthetic
// alert generator below, but real wiring will dispatch from the
// WebSocket subscriber when an alert event arrives over the wire.
//
//   useToast().push({
//     id: 'a-1234',
//     severity: 'critical' | 'warning' | 'info',
//     title: 'Terminal offline',
//     detail: 'Atlanta DC #2 — no heartbeat in 4 minutes',
//     to: '/terminals/UT-DST-VA-018-...',   // optional deep link
//     ttl: 8000,                            // optional auto-dismiss ms
//   })
//
// Toasts stack, auto-dismiss after their TTL, animate in/out, and
// can be manually dismissed via the × button. Click a toast body
// to follow its deep-link.

const ToastContext = createContext({ push: () => {}, dismiss: () => {} });
export function useToast() { return useContext(ToastContext); }

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counter = useRef(0);

  const dismiss = useCallback((id, { userInitiated = false } = {}) => {
    // Mark as leaving so the slide-out animation runs first.
    setToasts((cur) => {
      // If the user explicitly dismissed, remember the dedupe key so
      // upstream producers (alert stream) don't push it again this
      // session. Auto-TTL dismissals don't trigger this.
      const t = cur.find((x) => x.id === id);
      if (userInitiated && t?.dedupeKey) rememberToastDismissed(t.dedupeKey);
      return cur.map((t) => (t.id === id ? { ...t, leaving: true } : t));
    });
    setTimeout(() => setToasts((cur) => cur.filter((t) => t.id !== id)), 300);
  }, []);

  const push = useCallback((t) => {
    // Dedupe: if the caller supplies a dedupeKey (stable content hash),
    // we suppress when the user has dismissed it OR when the same key
    // was recently fired. Stops the demo stream from looking like a
    // broken record.
    if (t.dedupeKey) {
      if (wasToastDismissed(t.dedupeKey)) return;
      if (wasToastRecentlyFired(t.dedupeKey)) return;
      rememberToastFired(t.dedupeKey);
    }
    const id = t.id || `toast-${++counter.current}`;
    const toast = { ttl: 8000, severity: 'info', ...t, id };
    setToasts((cur) => [...cur, toast]);
    if (toast.ttl) setTimeout(() => dismiss(id), toast.ttl);
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ push, dismiss }}>
      {children}
      <ToastStack toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
}

// Toasts are viewport-fixed, so a hardcoded top offset only clears the
// navbar when nothing else sits above it. The demo/impersonation banners
// wrap to several lines on narrow screens and can run 200px+ tall — without
// this the toast stack rendered on top of (unreadable against) that banner
// text. Measure the real banner block instead of guessing a breakpoint.
function useBannerOffset() {
  const [offset, setOffset] = useState(64); // navbar height fallback
  useEffect(() => {
    const el = document.getElementById('app-top-banners');
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => setOffset(el.getBoundingClientRect().height + 64);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, []);
  return offset;
}

function ToastStack({ toasts, dismiss }) {
  const top = useBannerOffset();
  if (toasts.length === 0) return null;
  return (
    <div
      role="region"
      aria-live="polite"
      aria-label="Notifications"
      style={{ top }}
      className="fixed right-4 z-50 flex flex-col gap-2 w-[360px] max-w-[calc(100vw-2rem)] pointer-events-none"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={(opts) => dismiss(t.id, opts)} />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss }) {
  // Severity → accent color + side bar. Matches the rest of the
  // enterprise palette (red/amber/sky/emerald).
  const tones = {
    critical: { bar: 'bg-red-500',     ring: 'rgba(220,38,38,0.18)', label: 'Critical', labelCls: 'text-red-700' },
    warning:  { bar: 'bg-amber-500',   ring: 'rgba(245,158,11,0.18)', label: 'Warning',  labelCls: 'text-amber-800' },
    info:     { bar: 'bg-sky-500',     ring: 'rgba(14,165,233,0.18)', label: 'Info',     labelCls: 'text-sky-800' },
    success:  { bar: 'bg-emerald-500', ring: 'rgba(16,185,129,0.18)', label: 'Resolved', labelCls: 'text-emerald-700' },
  };
  const t = tones[toast.severity] || tones.info;

  const Body = ({ children }) =>
    toast.to ? (
      <Link to={toast.to} onClick={onDismiss} className="block flex-1 min-w-0 px-3 py-2.5 hover:bg-[var(--color-bg-hover)]">
        {children}
      </Link>
    ) : (
      <div className="flex-1 min-w-0 px-3 py-2.5">{children}</div>
    );

  return (
    <div
      className={`pointer-events-auto bg-white border border-[var(--color-line-1)] rounded-md overflow-hidden flex items-stretch ${toast.leaving ? 'toast-leave' : 'toast-enter'}`}
      style={{ boxShadow: `0 12px 28px -8px ${t.ring}, 0 1px 2px rgba(10,14,26,0.06)` }}
    >
      <div className={`w-1 ${t.bar} flex-shrink-0`} />
      <Body>
        <div className="flex items-center gap-2">
          <span className={`text-[11px] font-semibold tracking-normal ${t.labelCls}`}>{t.label}</span>
          <span className="text-[10px] text-[var(--color-ink-4)] font-medium">just now</span>
        </div>
        <div className="display text-[13px] font-semibold text-[var(--color-ink-1)] tracking-tight mt-0.5 truncate">
          {toast.title}
        </div>
        {toast.detail && (
          <div className="text-[12px] text-[var(--color-ink-3)] truncate mt-0.5">{toast.detail}</div>
        )}
      </Body>
      <button
        onClick={() => onDismiss({ userInitiated: true })}
        aria-label="Dismiss"
        className="pointer-events-auto text-[var(--color-ink-4)] hover:text-[var(--color-ink-1)] hover:bg-[var(--color-bg-hover)] px-2 self-stretch text-[14px] flex items-center"
      >
        ×
      </button>
    </div>
  );
}

// ============================================================
// useDemoAlertStream — synthetic toast generator for demo
// ============================================================
// Fires a random alert every ~45 seconds so the demo feels alive
// for sales calls. Only runs when getRuntimeMode().isProduction
// is false OR VITE_USE_MOCK_API === "true". When real WebSocket
// alerts come in, drop this hook and dispatch from the live
// subscriber instead.

// Each demo alert carries a stable dedupeKey. ToastCenter's `push` skips
// pushes when the key is in the session's dismissed-or-recently-fired
// set, so the user can't get the same toast 4 times in a 5-minute window.
const DEMO_ALERTS = [
  { dedupeKey: 'demo:atl-dc2-offline',    severity: 'critical', title: 'Terminal offline',          detail: 'Atlanta DC #2 · no heartbeat in 4m',                to: '/terminals' },
  { dedupeKey: 'demo:nyc-d5-sla',         severity: 'warning',  title: 'Latency above SLA target',  detail: 'New York Distribution #5 · 142ms / target 60ms',     to: '/terminals' },
  { dedupeKey: 'demo:hou-mob4-quota',     severity: 'warning',  title: 'Service line near quota',   detail: 'Houston Mobile fleet #4 · 89% of monthly allocation', to: '/service-lines' },
  { dedupeKey: 'demo:firmware-available', severity: 'info',     title: 'Firmware update available', detail: 'Kit firmware 2026.05.27 ready for 12 terminals',     to: '/firmware' },
  { dedupeKey: 'demo:phx-pop7-back',      severity: 'success',  title: 'Terminal back online',      detail: 'Phoenix Pop-up #7 · auto-recovered after 12m',       to: '/terminals' },
  { dedupeKey: 'demo:nct003-data-spike',  severity: 'warning',  title: 'SIM data spike detected',   detail: 'Truck-NCT-003 · 4.8GB in last 24h (5x normal)',      to: '/sims' },
  { dedupeKey: 'demo:dal-r12-failover',   severity: 'critical', title: 'WAN failover triggered',    detail: 'Dallas Retail #12 · Starlink → SIM backup active',  to: '/terminals' },
];

export function useDemoAlertStream({ enabled = true, intervalMs = 45000 } = {}) {
  const { push } = useToast();
  useEffect(() => {
    if (!enabled) return undefined;
    // Fire one quick toast 6s after mount so the first-load demo
    // immediately shows the live-feed cue, then settle into the
    // regular cadence.
    const intro = setTimeout(() => {
      const pick = DEMO_ALERTS[Math.floor(Math.random() * DEMO_ALERTS.length)];
      push(pick);
    }, 6000);
    const id = setInterval(() => {
      const pick = DEMO_ALERTS[Math.floor(Math.random() * DEMO_ALERTS.length)];
      push(pick);
    }, intervalMs);
    return () => { clearTimeout(intro); clearInterval(id); };
  }, [enabled, intervalMs, push]);
}
